import '@material/web/button/outlined-button.js'

import { css, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { i18next } from '@operato/i18n'
import { OxPopupList } from '@operato/popup'

import { FilterConfigObject, FilterPreference, PersonalGristPreference } from '../types.js'

import { OxFiltersForm } from '../filters/filters-form.js'

@customElement('ox-grist-filter-personalizer')
export class OxGristFilterPersonalizer extends LitElement {
  static styles = [
    css`
      md-icon {
        --md-icon-size: 16px;
        width: 36px;
        height: 36px;
        color: var(--md-sys-color-secondary-container);
        cursor: pointer;

        display: flex;
        place-content: center;
        place-items: center;
        position: relative;

        &:hover {
          color: var(--md-sys-color-primary-container);
        }
      }

      md-ripple {
        border-radius: 50%;
        inset: unset;
        height: 36px;
        width: 36px;
      }
    `
  ]

  @property({ type: Boolean, attribute: true }) debug: boolean = false

  @state() private preference?: PersonalGristPreference

  render() {
    return html`
      <md-icon
        @click=${async (e: MouseEvent) => {
          const form = this.closest('ox-filters-form') as OxFiltersForm
          const { filterColumns, personalFilters = [] } = form
          const queryFilters = await form.getQueryFilters()

          this.preference = {
            filters: (
              personalFilters.map((filter: FilterPreference) => {
                const originFilterColumn =
                  filter.name == 'search'
                    ? { filter: { name: 'search' } }
                    : filterColumns.find(f => f.name == filter.name)

                if (!originFilterColumn) {
                  /* 원래 filters 설정에 있는 것들 만을 유지한다. */
                  return
                }

                const { value } = originFilterColumn.filter! as FilterConfigObject

                return {
                  name: filter.name,
                  hidden: filter.hidden,
                  /* 만약, filters에 기본값이 이미 설정되어 있다면, 그대로 유지한다. */
                  value: value ?? queryFilters.find(f => f.name == filter.name)?.value
                }
              }) || []
            ).filter(Boolean) as FilterPreference[]
          }

          const template = html`
            <div class="personalizer-header" slot="header">
              <md-icon
                style="margin-left: auto;"
                @click=${async (e: MouseEvent) => {
                  if (form.personalConfigProvider) {
                    form.personalConfig = await form.personalConfigProvider.save(this.preference)
                  }
                  popup.close()
                }}
                title=${String(i18next.t('button.save'))}
                >keep</md-icon
              ><md-icon
                @click=${async (e: MouseEvent) => {
                  if (form.personalConfigProvider) {
                    form.personalConfig = this.preference = {}
                    await form.personalConfigProvider.reset()
                  }
                  popup.close()
                }}
                title=${String(i18next.t('button.delete'))}
                >keep_off</md-icon
              ><md-icon @click=${async (e: MouseEvent) => popup.close()} title=${String(i18next.t('button.close'))}
                >close</md-icon
              >
            </div>

            ${this.preference?.filters!.map(
              filter => html`
                <ox-checkbox label="checkbox" ?checked=${!filter.hidden} value=${filter.name} option
                  >${filter.name}<span style="position: absolute; right: 10px; cursor: row-resize;opacity:.5" handle
                    >☰</span
                  ></ox-checkbox
                >
              `
            )}
          `

          const popup = OxPopupList.open({
            template,
            multiple: true,
            sortable: true,
            debug: this.debug,
            attrSelected: 'checked',
            top: e.pageY,
            left: e.pageX,
            styles: css`
              :host {
                width: 240px;
                min-height: 300px;
                max-height: 80%;
                overflow: auto;
              }

              ::slotted(.personalizer-header) {
                --md-icon-size: 1.4em;

                display: flex;
                flex-direction: row;
                align-items: center;
                text-transform: capitalize;
                box-shadow: 0 3px 3px rgba(0, 0, 0, 0.3);
              }

              ::slotted([option]) {
                position: relative;
                user-select: none;
              }
            `
          })

          popup.onselect = (e: Event) => {
            const selected = (e as CustomEvent).detail

            const pconfig: PersonalGristPreference = { ...form.personalConfig }
            const pfilters = this.preference?.filters!

            pconfig.filters = pfilters.map(filter => {
              return {
                name: filter.name,
                hidden: selected.indexOf(filter.name) == -1,
                value: filter.value
              }
            })

            form.personalConfig = this.preference = pconfig

            form.applyUpdatedConfiguration()
          }

          popup.addEventListener('sorted', (e: Event) => {
            const sorted = (e as CustomEvent).detail as HTMLElement[]

            const pconfig: PersonalGristPreference = { ...form.personalConfig }
            const pfilters = this.preference?.filters!

            pconfig.filters = sorted
              .map(element => {
                const name = (element as HTMLInputElement).value
                return pfilters.find(filter => filter.name == name)!
              })
              .filter(Boolean)

            form.personalConfig = this.preference = pconfig

            form.applyUpdatedConfiguration()
          })
        }}
        >settings<md-ripple></md-ripple
      ></md-icon>
    `
  }
}
